import { useEffect, useMemo, useState } from "react";
import { Barbell, MagnifyingGlass, FunnelSimple } from "@phosphor-icons/react";
import { API } from "./constants";

// A program is considered "active" (scheda attiva) when it isn't completed
// and isn't a future-scheduled one waiting to start.
function isProgramActive(program) {
  if (!program) return false;
  if (program.status === "completata") return false;
  const hasDates = !!(program.start_date && program.end_date);
  if (hasDates && program.status === "da_fare") return false;
  return true;
}

export function ExercisesView({ exercises, token, programs = [], workouts = [] }) {
  const [search, setSearch] = useState("");
  const [muscleFilter, setMuscleFilter] = useState("all");
  const [equipmentFilter, setEquipmentFilter] = useState("all");
  const [onlyInProgram, setOnlyInProgram] = useState(false);
  const [mediaUrls, setMediaUrls] = useState({});
  const [previewId, setPreviewId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    exercises.forEach(async (ex) => {
      if (ex.media_path && !mediaUrls[ex.id]) {
        try {
          const resp = await fetch(`${API}/shared/${token}/files/${ex.media_path}`);
          if (resp.ok) {
            const blob = await resp.blob();
            setMediaUrls((prev) => ({ ...prev, [ex.id]: URL.createObjectURL(blob) }));
          }
        } catch (err) { console.debug("[ExercisesView]", err); }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercises, token]);

  // Unique muscle groups / equipments available across the library.
  const { muscleOptions, equipmentOptions } = useMemo(() => {
    const muscles = new Set();
    const equipments = new Set();
    exercises.forEach((ex) => {
      if (ex.muscle_group) muscles.add(ex.muscle_group);
      if (ex.equipment) equipments.add(ex.equipment);
    });
    return {
      muscleOptions: Array.from(muscles).sort(),
      equipmentOptions: Array.from(equipments).sort(),
    };
  }, [exercises]);

  // Names of exercises used in workouts of currently active programs.
  const inProgramNames = useMemo(() => {
    const activeProgramIds = new Set(
      programs.filter(isProgramActive).map((p) => p.id)
    );
    const names = new Set();
    workouts.forEach((w) => {
      if (!activeProgramIds.has(w.program_id)) return;
      (w.exercises || []).forEach((wex) => {
        if (wex?.name) names.add(wex.name.trim().toLowerCase());
      });
    });
    return names;
  }, [programs, workouts]);

  const filtered = exercises.filter((ex) => {
    if (!ex.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (muscleFilter !== "all" && ex.muscle_group !== muscleFilter) return false;
    if (equipmentFilter !== "all" && ex.equipment !== equipmentFilter) return false;
    if (onlyInProgram && !inProgramNames.has(ex.name.trim().toLowerCase())) return false;
    return true;
  });

  const hasActiveFilters =
    muscleFilter !== "all" || equipmentFilter !== "all" || onlyInProgram;

  return (
    <div className="space-y-3">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca esercizio..."
          className="w-full pl-9 pr-4 py-2.5 bg-[#171717] border border-[#262626] rounded-xl text-base text-white placeholder:text-zinc-600 outline-none focus:border-[#007AFF] transition-colors"
          data-testid="shared-exercise-search"
        />
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2">
        <FunnelSimple size={14} className="text-zinc-500" />
        <select
          value={muscleFilter}
          onChange={(e) => setMuscleFilter(e.target.value)}
          className="flex-1 min-w-[120px] px-2.5 py-1.5 bg-[#171717] border border-[#262626] rounded-lg text-xs text-white outline-none focus:border-[#007AFF]"
          data-testid="shared-exercise-muscle-filter"
        >
          <option value="all">Tutti i muscoli</option>
          {muscleOptions.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select
          value={equipmentFilter}
          onChange={(e) => setEquipmentFilter(e.target.value)}
          className="flex-1 min-w-[120px] px-2.5 py-1.5 bg-[#171717] border border-[#262626] rounded-lg text-xs text-white outline-none focus:border-[#007AFF]"
          data-testid="shared-exercise-equipment-filter"
        >
          <option value="all">Tutti gli attrezzi</option>
          {equipmentOptions.map((eq) => (
            <option key={eq} value={eq}>{eq}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setOnlyInProgram((v) => !v)}
          className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
            onlyInProgram
              ? "bg-[#007AFF]/15 border-[#007AFF]/40 text-[#007AFF]"
              : "bg-[#171717] border-[#262626] text-zinc-400 hover:text-white"
          }`}
          data-testid="shared-exercise-in-program-filter"
          aria-pressed={onlyInProgram}
        >
          In scheda
        </button>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setMuscleFilter("all");
              setEquipmentFilter("all");
              setOnlyInProgram(false);
            }}
            className="text-[11px] text-zinc-500 hover:text-white underline-offset-2 hover:underline"
            data-testid="shared-exercise-reset-filters"
          >
            Reset
          </button>
        )}
      </div>

      {filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((ex) => (
            <div key={ex.id} className="bg-[#171717] border border-[#262626] rounded-xl overflow-hidden" data-testid={`shared-exercise-${ex.id}`}>
              {mediaUrls[ex.id] && (
                <div
                  className="aspect-video bg-[#0A0A0A] cursor-pointer"
                  onClick={() => setPreviewId(previewId === ex.id ? null : ex.id)}
                >
                  <img src={mediaUrls[ex.id]} alt={ex.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-3.5">
                <button
                  onClick={() => setExpandedId(expandedId === ex.id ? null : ex.id)}
                  className="flex items-center gap-2.5 w-full text-left"
                >
                  <div className="w-7 h-7 rounded-md bg-[#007AFF]/15 flex items-center justify-center flex-shrink-0">
                    <Barbell size={14} weight="fill" className="text-[#007AFF]" />
                  </div>
                  <h4 className="font-semibold text-white text-sm">{ex.name}</h4>
                </button>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {ex.category && <span className="text-[10px] px-1.5 py-0.5 bg-[#007AFF]/10 text-[#007AFF] rounded-full font-medium">{ex.category}</span>}
                  {ex.muscle_group && <span className="text-[10px] px-1.5 py-0.5 bg-[#D4FF00]/10 text-[#D4FF00] rounded-full font-medium">{ex.muscle_group}</span>}
                  {(Array.isArray(ex.secondary_muscle_groups) && ex.secondary_muscle_groups.length > 0
                    ? ex.secondary_muscle_groups
                    : (ex.secondary_muscle_group ? [ex.secondary_muscle_group] : [])
                  ).map((sg) => (
                    <span key={`sg-${sg}`} className="text-[10px] px-1.5 py-0.5 bg-[#34C759]/10 text-[#34C759] rounded-full font-medium">{sg}</span>
                  ))}
                  {ex.equipment && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-700/50 text-zinc-400 rounded-full font-medium">{ex.equipment}</span>}
                </div>
                {ex.description && expandedId === ex.id && (
                  <div className="mt-3 p-3 bg-[#0A0A0A] rounded-lg border border-[#1E1E1E]">
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1">Descrizione</p>
                    <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">{ex.description}</p>
                  </div>
                )}
                {ex.description && expandedId !== ex.id && (
                  <p
                    className="text-[11px] text-zinc-600 mt-2 line-clamp-1 cursor-pointer"
                    onClick={() => setExpandedId(ex.id)}
                  >
                    {ex.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-zinc-500 text-sm">
          {search || hasActiveFilters ? "Nessun esercizio trovato" : "Nessun esercizio disponibile"}
        </div>
      )}
    </div>
  );
}
